import {
    buildSnapAnchors,
    resolveSnapTierHeightVh,
} from "../../../components/ItinerariesPlanner/panelResize/panelResize.tools";
import type { PanelResizeProfileId, PanelSnapTier } from "../../../components/ItinerariesPlanner/panelResize/panelResize.types";
import { PANEL_RESIZE_PROFILES } from "../../../config/itineraryPanel.configuration";
import { ephemeralStore } from "../runtime/ephemeralStore";
import { itineraryPanelStateSlice } from "./itineraryPanelState.slice";

/**
 * Begins a live resize session with the given starting height.
 *
 * @param startHeightVh - Height (vh) seeded from the rendered panel at pointer down.
 * @param profileId - Surface profile initiating the drag.
 */
export const beginPanelDrag = ( startHeightVh: number, profileId: PanelResizeProfileId ): void => {
    ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => ( {
        ...previous,
        "dragHeightVh":          startHeightVh,
        "isDragging":            true,
        "activeResizeProfileId": profileId,
    } ) );
};

/**
 * Updates the live drag height during pointer movement.
 *
 * @param heightVh - Candidate live height (vh).
 */
export const updatePanelDragHeight = ( heightVh: number ): void => {
    ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => {
        if ( previous.dragHeightVh !== null && Object.is( previous.dragHeightVh, heightVh ) ) {
            return previous;
        }

        return {
            ...previous,
            "dragHeightVh": heightVh,
        };
    } );
};

/**
 * Commits a snap tier after pointer release or keyboard snap.
 *
 * @param tier - Target snap tier.
 * @param resolvedHeightVh - Committed height (vh) for the tier.
 * @param profileId - Surface profile that committed the snap.
 */
export const commitPanelSnapTier = (
    tier: PanelSnapTier,
    resolvedHeightVh: number,
    profileId: PanelResizeProfileId,
): void => {
    ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => ( {
        ...previous,
        "panelSnapTier":         tier,
        "itineraryListHeight":   resolvedHeightVh,
        "dragHeightVh":          null,
        "isDragging":            false,
        "activeResizeProfileId": profileId,
        ...profileId === "itineraryDetails"
            ? { "rememberedItineraryDetailsSnapTier": tier }
            : { "rememberedItineraryListSnapTier": tier },
    } ) );
};

/**
 * Ends a drag session without committing a new snap tier.
 *
 * @remarks
 * Used on `pointercancel` and component unmount while dragging.
 */
export const endPanelDragWithoutCommit = (): void => {
    ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => {
        if ( !previous.isDragging && previous.dragHeightVh === null ) {
            return previous;
        }

        return {
            ...previous,
            "dragHeightVh": null,
            "isDragging":   false,
        };
    } );
};

/**
 * Commits a snap tier using profile anchors and the current maximized measurement.
 *
 * @param tier - Target snap tier.
 * @param maximizedPanelHeightVh - Dynamic maximized anchor (vh).
 * @param profileId - Surface profile to record on commit.
 */
export const commitPanelSnapTierFromProfile = (
    tier: PanelSnapTier,
    maximizedPanelHeightVh: number,
    profileId: PanelResizeProfileId,
): void => {
    const profile = PANEL_RESIZE_PROFILES[profileId];
    const anchors = buildSnapAnchors( profile, maximizedPanelHeightVh );
    const resolvedHeightVh = resolveSnapTierHeightVh( tier, anchors );
    commitPanelSnapTier( tier, resolvedHeightVh, profileId );
};

/**
 * Resets the itinerary list to the expanded tier (new search, map pick, etc.).
 *
 * @remarks
 * Also updates {@link ItineraryPanelState.rememberedItineraryListSnapTier}. Itinerary-details memory
 * is left unchanged.
 *
 * @param maximizedPanelHeightVh - Dynamic maximized anchor (vh) for anchor resolution.
 */
export const resetPanelToExpandedTier = ( maximizedPanelHeightVh: number ): void => {
    commitPanelSnapTierFromProfile( "expanded", maximizedPanelHeightVh, "itineraryList" );
};

/**
 * Switches to itinerary details and restores its last remembered snap tier.
 *
 * @remarks
 * Persists the current list tier into {@link ItineraryPanelState.rememberedItineraryListSnapTier}
 * before applying itinerary-details memory.
 *
 * @param maximizedPanelHeightVh - Dynamic maximized anchor (vh) for anchor resolution.
 */
export const restoreRememberedItineraryDetailsSnapTier = ( maximizedPanelHeightVh: number ): void => {
    ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => ( {
        ...previous,
        "rememberedItineraryListSnapTier": previous.panelSnapTier,
    } ) );

    const rememberedTier = ephemeralStore.peek( itineraryPanelStateSlice ).rememberedItineraryDetailsSnapTier;
    commitPanelSnapTierFromProfile( rememberedTier, maximizedPanelHeightVh, "itineraryDetails" );
};

/**
 * Switches back to the itinerary list and restores its last remembered snap tier.
 *
 * @remarks
 * Persists the current itinerary-details tier into {@link ItineraryPanelState.rememberedItineraryDetailsSnapTier}
 * before applying list memory.
 *
 * @param maximizedPanelHeightVh - Dynamic maximized anchor (vh) for anchor resolution.
 */
export const restoreRememberedItineraryListSnapTier = ( maximizedPanelHeightVh: number ): void => {
    ephemeralStore.setValue( itineraryPanelStateSlice, ( previous ) => ( {
        ...previous,
        "rememberedItineraryDetailsSnapTier": previous.panelSnapTier,
    } ) );

    const rememberedTier = ephemeralStore.peek( itineraryPanelStateSlice ).rememberedItineraryListSnapTier;
    commitPanelSnapTierFromProfile( rememberedTier, maximizedPanelHeightVh, "itineraryList" );
};
